/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { Platform } from 'react-native';
import { useAppConfig, useSuperAppContext } from '@airasia-rn/ui';
import { useOptionalSnapConfig } from '../configs/SnapConfigProvider';
import { resolveRecentSearch } from '../utils';
import {
  DEFAULT_GEO_ID,
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
} from '../configs/constants';
import type { SnapRecentSearchEntry } from '../types';
import {
  buildSnapRecentSearchListingUrl,
  buildSnapRecentSearchNativeListingProps,
  type SnapNativeListingProps,
} from '../utils/listingUrl';
import { useSnapSearchViewFeatureFlag } from './useSnapSearchViewFeatureFlag';

interface UseRecentSearchNavigationOptions {
  navigateToUrl: (
    url: string,
    nativeListingProps?: SnapNativeListingProps,
  ) => void;
}

type NativeSnapOpenModulePayload = {
  widget: string;
  deeplink?: string;
  snapListingProps?: SnapNativeListingProps;
};

type NativeSnapListingContext = ReturnType<typeof useSuperAppContext> & {
  openNativeSnapListing?: boolean;
  isLoggedIn?: boolean;
  openModule?: (data: NativeSnapOpenModulePayload) => void;
};

/**
 * Hook for handling recent search card press navigation
 * Builds URL from recent search entry and navigates to it
 */
export const useRecentSearchNavigation = ({
  navigateToUrl,
}: UseRecentSearchNavigationOptions) => {
  const { config: appConfig } = useAppConfig();
  const { env, currency, locale, geoId: appGeoId } = appConfig;
  const snapConfig = useOptionalSnapConfig()?.config;
  const superAppContext = useSuperAppContext() as NativeSnapListingContext;
  const { hotel_snap_unified_listing_enabled } = useSnapSearchViewFeatureFlag();

  const handleRecentSearchCardPress = useCallback(
    (entry: SnapRecentSearchEntry) => {
      const resolved = resolveRecentSearch(entry);
      const resolvedCountryCode =
        snapConfig?.geoId ??
        superAppContext.geoId ??
        appGeoId ??
        DEFAULT_GEO_ID;
      const resolvedCurrency = currency || DEFAULT_CURRENCY;
      const resolvedLanguage = locale || DEFAULT_LANGUAGE;

      const listingUrl = buildSnapRecentSearchListingUrl({
        entry: resolved,
        env,
        countryCode: resolvedCountryCode,
        currency: resolvedCurrency,
        language: resolvedLanguage,
        hotel_snap_unified_listing_enabled,
      });
      const nativeListingProps = buildSnapRecentSearchNativeListingProps({
        entry: resolved,
        countryCode: resolvedCountryCode,
        currency: resolvedCurrency,
        language: resolvedLanguage,
        isLoggedIn: superAppContext.isLoggedIn ?? false,
      });

      // Keep recent searches on the unified Snap list when it is enabled,
      // matching the primary Search button. The native module below opens the
      // legacy listing screen.
      if (Platform.OS !== 'web' && hotel_snap_unified_listing_enabled) {
        navigateToUrl(listingUrl, nativeListingProps);
        return;
      }

      if (
        Platform.OS !== 'web' &&
        superAppContext.openNativeSnapListing &&
        superAppContext.openModule
      ) {
        superAppContext.openModule({
          widget: 'snap_deeplink',
          deeplink: listingUrl,
          snapListingProps: nativeListingProps,
        });
        return;
      }

      navigateToUrl(listingUrl, nativeListingProps);
    },
    [
      appGeoId,
      currency,
      env,
      locale,
      snapConfig?.geoId,
      superAppContext.geoId,
      superAppContext.isLoggedIn,
      superAppContext.openModule,
      superAppContext.openNativeSnapListing,
      hotel_snap_unified_listing_enabled,
      navigateToUrl,
    ],
  );

  return { handleRecentSearchCardPress };
};
